import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { arrotondaSetpoint, validaSetpoint, type Zona } from '@emacomfort/domain';
import { useComando, useDevice, useLettura, useObsoleto, useStato } from '@/app/DeviceProvider';
import { useFormato } from '@/app/formato';
import { Avviso, Badge, Card, Interruttore, StatoComandoVista, Stepper, Valore, cx, stili } from '@/ui';
import { IconaZonaVista } from '@/ui/icone';
import { useValoreDifferito } from '../comune';
import s from '../features.module.css';

export function ZoneCard({ zona }: { zona: Zona }) {
  const { t } = useTranslation();
  const f = useFormato();
  const { invia } = useDevice();
  const lettura = useLettura(zona.canaleSonda);
  const { obsoleto } = useObsoleto();
  const inDomanda = useStato((st) => st.stato?.zoneInDomanda.includes(zona.id) ?? false);
  const comando = useComando(`zona:${zona.id}`);
  const [errore, setErrore] = useState<string | null>(null);

  const setpoint = useValoreDifferito(zona.setpoint, (v) => invia({ azione: 'set_setpoint', zonaId: zona.id, valore: v }));

  useEffect(() => {
    if (!errore) return;
    const id = setTimeout(() => setErrore(null), 5000);
    return () => clearTimeout(id);
  }, [errore]);

  const cambia = (delta: number) => {
    const nuovo = arrotondaSetpoint(setpoint.valore + delta);
    const esito = validaSetpoint(nuovo, zona.limiti);
    if (!esito.ok) {
      // RF-10b: non inviato, e l'interfaccia spiega qual è il limite e chi lo ha impostato.
      setErrore(t('zone.fuoriLimite', { min: f.temp(zona.limiti.min, 0), max: f.temp(zona.limiti.max, 0), chi: zona.limiti.impostatiDa }));
      return;
    }
    setErrore(null);
    setpoint.imposta(nuovo);
  };

  return (
    <Card className={cx(s.zona, !zona.abilitata && s.zonaSpenta)}>
      <div className={s.zonaTestata}>
        <span className={s.zonaNome}>
          <IconaZonaVista icona={zona.icona} size={26} aria-hidden />
          {zona.nome}
        </span>
        <Interruttore
          acceso={zona.abilitata}
          onChange={(v) => void invia({ azione: 'set_zona_abilitata', zonaId: zona.id, abilitata: v })}
          etichetta={zona.abilitata ? t('zone.spegni', { zona: zona.nome }) : t('zone.accendi', { zona: zona.nome })}
          testoAcceso={t('zone.attiva')}
          testoSpento={t('zone.spenta')}
          disabilitato={obsoleto}
        />
      </div>
      <div className={s.zonaCorpo}>
        <div className={s.lettura}>
          <span className={stili.etichetta}>{t('zone.tempAttuale')}</span>
          <Valore numero={f.temp(lettura.valore)} dimensione="secondario" obsoleto={lettura.obsoleto} />
          {lettura.q === 'sonda_scollegata' && <span className={s.letturaNota}>{t('stato.sondaScollegata')}</span>}
        </div>
        <div className={s.lettura}>
          <span className={stili.etichetta}>{t('zone.tempDesiderata')}</span>
          <Stepper
            valore={f.tempNumero(setpoint.valore)}
            onMeno={() => cambia(-0.5)}
            onPiu={() => cambia(0.5)}
            etichettaMeno={t('zone.meno', { zona: zona.nome })}
            etichettaPiu={t('zone.piu', { zona: zona.nome })}
            disabilitato={obsoleto || !zona.abilitata}
          />
        </div>
      </div>
      <div className={s.zonaStati}>
        {zona.abilitata && !obsoleto && (inDomanda ? <Badge tono="info">{t('zone.inDomanda')}</Badge> : <Badge tono="ok">{t('zone.aRegime')}</Badge>)}
        {zona.sovrascritta && <Badge tono="neutro">{t('zone.sovrascritta')}</Badge>}
        <StatoComandoVista esito={setpoint.inAttesa && !comando ? 'in_invio' : comando?.esito} motivo={comando?.motivo} />
      </div>
      {errore && <Avviso tono="warn">{errore}</Avviso>}
    </Card>
  );
}
